import { useMemo } from "react";
import { Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { DocumentLoadStatus, type DocumentLoad, type TargetSchema } from "@/types";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { ENTITY_CONFIG, STATUS_CONFIG, countOperations, formatOperationSummary } from "./config";
import { groupLoadedRecordsBySchema } from "./SchemaResultCard";
import { FailedRecordsAccordion } from "./FailedRecordsAccordion";

interface LoadDetailsDialogProps {
  load: DocumentLoad | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Drill-down on a single historical DocumentLoad.
 *
 * Triggered from PreviousLoadsDrawer where each prior load is just a
 * status + timestamp + counts summary. This dialog answers the natural
 * follow-up question — "what specifically failed?" — by surfacing the
 * full per-schema breakdown and grouped failure messages.
 *
 * Sections (each renders only when its data is present):
 *
 *   - Header: status, timestamp, top-line counts
 *   - Loaded by schema: counts-only breakdown (the actual entities are
 *     latest-load territory; here we just want to know "what shape of
 *     work happened" — e.g. "1 created · 1 updated")
 *   - Failed records: grouped per-schema via the existing
 *     FailedRecordsAccordion, expanded by default so the diagnostic
 *     reads inline.
 */
export const LoadDetailsDialog = ({ load, open, onClose }: LoadDetailsDialogProps) => {
  // groupedLoaded uses the load's loaded_records to derive per-schema operation
  // counts. Keep the hook call unconditional so React's hook-order rules hold
  // even when `load` is briefly null between selection and unmount.
  const groupedLoaded = useMemo(() => groupLoadedRecordsBySchema(load?.loaded_records ?? []), [load?.loaded_records]);

  const failedBySchema = useMemo(() => {
    const map = new Map<TargetSchema, NonNullable<DocumentLoad["failed_records"]>>();
    for (const record of load?.failed_records ?? []) {
      const list = map.get(record.target_schema) ?? [];
      list.push(record);
      map.set(record.target_schema, list);
    }
    return map;
  }, [load?.failed_records]);

  if (!load) return null;

  // Fall back to FAILED treatment for unknown statuses — keeps the dialog
  // from crashing if the backend emits a status not in the local enum
  // (new value, legacy record). Same pattern in PreviousLoadsDrawer.
  const statusConfig = STATUS_CONFIG[load.status] ?? STATUS_CONFIG[DocumentLoadStatus.FAILED];
  const StatusIcon = statusConfig.icon;
  const loadedCount = load.loaded_records?.length ?? 0;
  const failedCount = load.failed_records?.length ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        Load details
        <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Header: status chip + timestamp + top-line counts */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
              <Chip
                icon={<StatusIcon sx={{ fontSize: 16 }} />}
                label={statusConfig.label}
                size="small"
                color={statusConfig.color}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              {load.created_datetime && (
                <Typography variant="body2" color="text.secondary">
                  {formatDate(load.created_datetime, DATE_FORMATS.DISPLAY_LONG_WITH_TIME)}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {loadedCount} loaded{failedCount > 0 ? `, ${failedCount} failed` : ""}
            </Typography>
          </Box>

          {/* Loaded by schema — counts only. The latest-load Result tab owns
              per-record drill-down; here, prior-load entities may be stale or
              superseded, so a per-schema operation summary is more
              appropriate than enumerating individual records. */}
          {groupedLoaded.length > 0 && (
            <Section title="Loaded">
              <Stack spacing={0.75}>
                {groupedLoaded.map((group) => {
                  const counts = countOperations(group.operations);
                  const config = ENTITY_CONFIG[group.targetSchema];
                  const total = group.entityIds.length;
                  return (
                    <Box
                      key={group.targetSchema}
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        py: 0.75,
                        px: 1,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {config?.labelPlural ?? String(group.targetSchema)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {total} record{total !== 1 ? "s" : ""}
                        {formatOperationSummary(counts) && (
                          <Box component="span" sx={{ ml: 0.5 }}>
                            · {formatOperationSummary(counts)}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Section>
          )}

          {/* Failed records — the diagnostic-value section. Re-uses the same
              accordion the Result tab uses for current-load failures so the
              error treatment stays consistent across surfaces. */}
          {failedCount > 0 && (
            <Section title={`Failed records (${failedCount})`}>
              <Stack spacing={1}>
                {[...failedBySchema.entries()].map(([schema, records]) => (
                  <FailedRecordsAccordion
                    key={schema}
                    failedRecords={records}
                    schemaLabel={ENTITY_CONFIG[schema]?.labelPlural ?? String(schema)}
                  />
                ))}
              </Stack>
            </Section>
          )}

          {/* Clean load — nothing else to show; surface a positive marker so
              the dialog doesn't read as if data is missing. */}
          {loadedCount === 0 && failedCount === 0 && (
            <Typography variant="body2" color="text.secondary">
              This load processed without producing records or failures.
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <Box>
    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);
